import { matchPath } from 'react-router-dom';
import { IconBell, IconBellBolt, IconNetwork } from '@tabler/icons-react';
import { SettingsNavItemConfig, SettingsSection } from '../interfaces/settingsNav';

/** The route trees the settings sidebar frames; also what lights the appbar's gear. */
export const SETTINGS_ROUTE_PATTERNS = ['/account/*', '/team/*', '/payment/*', '/alerts/*'];

/**
 * The one answer to "is this a settings route". `matchPath` because that is what `Routes`
 * itself uses — it matches case-insensitively, so `/Team/members` renders the Team page and
 * must read as a settings route here too.
 */
export function isSettingsRoute(pathname: string): boolean {
  return SETTINGS_ROUTE_PATTERNS.some(pattern => !!matchPath(pattern, pathname));
}

/** Where Settings lands; the Payment page's index redirect resolves it to Plan Details (or Usage Summary). */
export const SETTINGS_HOME_PATH = '/payment';

export const SETTINGS_SECTION_ORDER: SettingsSection[] = [
  SettingsSection.ALERTS,
  SettingsSection.BILLING,
  SettingsSection.USER,
  SettingsSection.TEAM
];

export const SETTINGS_SECTION_TITLES: Record<SettingsSection, string> = {
  [SettingsSection.ALERTS]: 'Alerts',
  [SettingsSection.BILLING]: 'Billing',
  [SettingsSection.USER]: 'User',
  [SettingsSection.TEAM]: 'Team'
};

/*
 * Labels, routes and data-ids mirror the routes each page mounts (ACCOUNT_TABS, the Team routes,
 * the Payment and Alerts tab memos). Kept as plain descriptors here rather than importing those
 * lists: they carry every page component, and this file is loaded by the dashboard shell, so
 * importing them would pull all four route chunks into the main bundle. `settingsNav.test.ts`
 * holds the Account list in step.
 */
/** Billing is absent on purpose: it is resolved from the plan by `BillingNavProvider`. */
export const SETTINGS_NAV: Partial<Record<SettingsSection, SettingsNavItemConfig[]>> = {
  /*
   * Standard and Edge were the old top-level tab strip; their second-level tabs hang under them
   * as groups. The Edge page keeps its recipient switcher above the content — it is mounted once
   * for both children, so the chosen recipient carries across them.
   */
  [SettingsSection.ALERTS]: [
    {
      label: 'Standard',
      path: '/alerts/preferences',
      dataId: 'standard-tab',
      SVGIcon: IconBell,
      children: [
        {
          label: 'Preferences',
          path: '/alerts/preferences',
          dataId: 'preferences',
          icon: 'preferences'
        },
        {
          label: 'Integration for Slack',
          path: '/alerts/slack',
          dataId: 'integration-for-slack',
          icon: 'slack'
        },
        {
          label: 'Webhook Events',
          path: '/alerts/webhooks',
          dataId: 'webhook-events',
          icon: 'webhook',
          visibleWhen: ctx => ctx.isWebhookEventsEnabled
        },
        {
          label: 'Audit Tables',
          path: '/alerts/audit-table',
          dataId: 'audit-tables',
          icon: 'table',
          visibleWhen: ctx => ctx.isAuditTablesEnabled
        }
      ]
    },
    {
      label: 'Edge',
      path: '/alerts/edge',
      dataId: 'edge-tab',
      SVGIcon: IconBellBolt,
      visibleWhen: ctx => ctx.isEdgeAlertsEnabled,
      children: [
        {
          // The data-id keeps the old "Alert Preferences" tab's slug.
          label: 'Preferences',
          path: '/alerts/edge/preferences',
          dataId: 'alert-preferences',
          icon: 'preferences'
        },
        {
          label: 'Subscriptions',
          path: '/alerts/edge/subscriptions',
          dataId: 'subscriptions',
          icon: 'checklist'
        }
      ]
    }
  ],

  [SettingsSection.USER]: [
    {
      label: 'Change Password',
      path: '/account/change-password',
      dataId: 'change-password',
      icon: 'password'
    },
    {
      label: 'Two Factor Authentication',
      path: '/account/tfa',
      dataId: 'two-factor-authentication',
      icon: 'tfa'
    },
    {
      label: 'API Keys',
      path: '/account/api-keys',
      dataId: 'api-keys',
      icon: 'key'
    },
    {
      label: 'Delete Account',
      path: '/account/delete-account',
      dataId: 'Delete Account',
      icon: 'delete',
      status: 'error'
    }
  ],

  [SettingsSection.TEAM]: [
    {
      label: 'Workspaces',
      path: '/team/workspaces',
      dataId: 'Workspaces',
      icon: 'plan',
      visibleWhen: ctx => ctx.isWorkspacesEnabled && ctx.isISVTeam
    },
    {
      label: 'Connectivity',
      path: '/team/connectivity',
      dataId: 'Connectivity',
      SVGIcon: IconNetwork,
      visibleWhen: ctx => ctx.isVPCPeeringConnectionEnabled
    },
    {
      label: 'Members',
      path: '/team/members',
      dataId: 'Members',
      icon: 'team'
    },
    {
      label: 'Activity Logs',
      path: '/team/cloudwatch',
      dataId: 'Activity Logs',
      icon: 'cloudwatch'
    },
    {
      label: 'Authentication',
      path: '/team/authentication',
      dataId: 'Authentication',
      icon: 'authentication',
      visibleWhen: ctx => ctx.isTeamEditor
    },
    {
      label: 'Timezone',
      path: '/team/timezone',
      dataId: 'Timezone',
      icon: 'timezone'
    },
    {
      label: 'Data & Privacy',
      path: '/team/account-access',
      dataId: 'Data & Privacy',
      icon: 'account-access'
    }
  ]
};
